import axios from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { ZodSchema } from 'zod';
import { msalConfig, apiScopes } from './msal-config';
import { readTenantSlug } from './tenantStorage';

// Resolve the API base. On Vercel *preview* builds (any non-production branch,
// e.g. `dev`) default to the dev API: the explicit NEXT_PUBLIC_API_URL is often
// scoped to Production only, so previews would otherwise inherit the prod URL —
// which blocks the preview origin (CORS) and runs older code. Production builds
// keep the explicit prod URL. Vercel auto-exposes NEXT_PUBLIC_VERCEL_ENV /
// NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF to the browser.
const DEV_API_URL = 'https://revpulse-api-dev.cluster-november.k.witailer.tech';

function resolveApiBase(): string {
  const explicit = process.env.NEXT_PUBLIC_API_URL;
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
  const branch = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF;
  if (vercelEnv === 'preview' || branch === 'dev') {
    return process.env.NEXT_PUBLIC_API_URL_DEV || DEV_API_URL;
  }
  return explicit || 'http://localhost:8000';
}

const apiClient = axios.create({
  baseURL: resolveApiBase(),
});

// Singleton — avoids creating a new instance on every request.
// Store the init *promise* (not the instance) so concurrent callers all await
// the same initialize() call instead of racing and using an uninitialized PCA.
// Prefer the global instance created by providers.tsx so we share auth state.
let _msalInitPromise: Promise<PublicClientApplication> | null = null;

async function getMsalInstance(): Promise<PublicClientApplication> {
  if (typeof window !== 'undefined' && window.__msalInstance) {
    return window.__msalInstance as PublicClientApplication;
  }
  if (!_msalInitPromise) {
    _msalInitPromise = (async () => {
      const instance = new PublicClientApplication(msalConfig);
      await instance.initialize();
      if (typeof window !== 'undefined') {
        window.__msalInstance = instance;
      }
      return instance;
    })();
  }
  return _msalInitPromise;
}

// Inject tenant slug from session/local storage. Group endpoints don't take
// a tenant context (they aggregate across all tenants), so skip the header
// only for those — not based on the slug value. Previously we skipped the
// header whenever slug==='__group__', but components like LastUploadBadge
// remain mounted during group mode and call non-group endpoints; those then
// silently fell back to the alphabetical-first tenant, polluting the cache.
apiClient.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const url = config.url || '';
    const isGroupEndpoint = url.startsWith('/api/v1/group/');
    // Caller-supplied header wins. Write operations (uploads, member CRUD)
    // pin the slug from React state to dodge any race between storage and
    // active tenant — see ManageDataPanel.handleSubmit. Don't clobber it.
    const explicit =
      (config.headers as Record<string, unknown> | undefined)?.['X-Tenant-Slug'];
    // Aggregate pseudo-slugs ('__group__', '__group__:<entity>') are not
    // real tenants — never send them as a tenant header.
    const isPseudo = (v: unknown) =>
      typeof v === 'string' && v.startsWith('__group__');
    if (explicit && !isPseudo(explicit) && !isGroupEndpoint) {
      return config;
    }
    const slug = readTenantSlug();
    if (slug && !isPseudo(slug) && !isGroupEndpoint) {
      config.headers['X-Tenant-Slug'] = slug;
    }
  }
  return config;
});

// Dedup concurrent token acquisitions. On a cold page load N components
// fire useQuery in parallel and each interceptor calls acquireTokenSilent;
// without dedup, MSAL serializes them (hidden-iframe throttle), turning a
// single ~300ms renew into a 10-15s waterfall. We share a single in-flight
// promise across all callers and clear it once it settles.
let _tokenPromise: Promise<string | null> | null = null;

async function getAccessToken(): Promise<string | null> {
  if (_tokenPromise) return _tokenPromise;
  _tokenPromise = (async () => {
    try {
      const instance = await getMsalInstance();
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) return null;
      // Active account is set by providers.tsx / login on redirect; fall back
      // to the first account only when none was marked active. accounts[0]
      // alone picks the wrong identity when multiple accounts are signed in.
      const account = instance.getActiveAccount() ?? accounts[0];
      const response = await instance.acquireTokenSilent({
        ...apiScopes,
        account,
      });
      return response.accessToken;
    } finally {
      // Release the slot on the next tick so callers in the same microtask
      // batch all reuse the same promise, while a later request gets a
      // fresh acquisition (MSAL's own cache will still serve it instantly
      // when the token is valid).
      setTimeout(() => {
        _tokenPromise = null;
      }, 0);
    }
  })();
  return _tokenPromise;
}

apiClient.interceptors.request.use(async (config) => {
  if (process.env.NEXT_PUBLIC_AUTH_DISABLED === 'true') return config;
  try {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Token expired and silent refresh not possible — redirect to login
      const instance = await getMsalInstance();
      await instance.acquireTokenRedirect(apiScopes);
      return Promise.reject(new Error('Redirecting for authentication'));
    }
    // Other errors (network, etc.) — let the request go through; API will return 401
    console.error('Token acquisition failed:', error);
  }
  return config;
});

// Redirect to login on 401 so the user doesn't get stuck on a blank page.
// Guard against the loop where /login itself fires API calls (e.g. TenantContext)
// that 401 → hard-redirect to /login → re-mount → 401 → …
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const onPublicPath = window.location.pathname.startsWith('/login')
        || window.location.pathname.startsWith('/sign-in');
      if (!onPublicPath) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Runtime-validated GET (plan item #23). When the API contract drifts, a
// failed Zod parse surfaces a clear error at the boundary instead of a
// confusing TypeError several components deep.
//
// Usage:
//   const data = await fetchValidated('/api/v1/dashboard/totals', KPITotalsSchema);
export async function fetchValidated<T>(
  path: string,
  schema: ZodSchema<T>,
  config?: Parameters<typeof apiClient.get>[1],
): Promise<T> {
  const { data } = await apiClient.get(path, config);
  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    if (process.env.NODE_ENV !== 'production') {
      // Loud in dev so the contract issue is obvious.
      console.error('[api] response validation failed', { path, issues: parsed.error.issues });
    }
    throw new Error(
      `API contract violation on ${path}: ${parsed.error.issues
        .map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`)
        .join('; ')}`,
    );
  }
  return parsed.data;
}

export default apiClient;
